import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '@/app/hooks';
import { makePostCall } from '@/services/makePostCall';
import { DefaultModal } from '@/components/Modals/DefaultModal/DefaultModal';
import { Container, Title, Content, BodyText, Footer, WrapperInput } from './VerificationCode.styles';
import { TextInput } from '../input/TextInput/TextInput';
import { Button } from '../Button/DefaultButton/Button';
import * as yup from 'yup';
import { Spinner } from '../RefreshableCurrencyInput/RefreshableCurrencyInput.styles';

// Type for loan application response
interface LoanApplicationResponse {
  loanId: string;
  loanNumber: string;
  loanType: string;
  boappLastStep?: string;
  [key: string]: any;
}

interface VerificationCodeProps {
  setIsOpenEnterCode: React.Dispatch<React.SetStateAction<boolean>>;
  email: string;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  sendCodeToEmail: () => Promise<void>;
  isOpenEnterCode: boolean;
  isNoApplicationsModalOpen: boolean;
  setApplications: React.Dispatch<React.SetStateAction<LoanApplicationResponse[]>>;
  setCanStartNewApp: React.Dispatch<React.SetStateAction<boolean>>;
}

type Config = {
  title: string;
  description: string;
  descriptionSecondary: string;
  labelBTN: string;
};

type Options = {
  existing?: boolean;
  isValidation?: boolean;
};

const VERIFICATION_CODE_CONFIG = {
  isHidden: false,
  text: 'Verification Code',
  buttonRestart: {
    isHidden: false,
    text: "Didn't receive a code? Resend it",
  },
};

const RESUME_CODE_REGEX = /^[a-zA-Z0-9]{6}$/;

export const VerificationCode = ({
  setIsOpenEnterCode: _setIsOpenEnterCode,
  email: email,
  setStep,
  sendCodeToEmail,
  setApplications,
  setCanStartNewApp,
}: VerificationCodeProps) => {
  const [value, setValue] = useState<string>('');
  const [isValid, setIsValid] = useState<boolean>(false);
  const [validated, setValidated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [timer, setTimer] = useState<number>(0);
  const [error, setError] = useState<string | unknown>('');
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const dispatch = useAppDispatch();
  const loanApplication = useAppSelector((state) => state.LoanApplication);
  const templateConfig = useAppSelector((state) => state.TemplateConfig);

  const existingApplication = false;

  const formSchema = useMemo(() => {
    const yupParams = yup
      .string()
      .transform((val) => val.replace(/[^a-zA-Z0-9]/g, ''))
      .matches(RESUME_CODE_REGEX, { excludeEmptyString: false });

    return yup.object().shape({
      'resume-code-input': yupParams,
    });
  }, []);

  const checkValidation = useCallback(
    async (inputData: Record<string, string>) => {
      const varIsValid = await formSchema.isValid(inputData);
      setIsValid(varIsValid);
    },
    [formSchema],
  );

  const verifyCode = useCallback(async () => {
    if (!isValid) return;

    try {
      setIsLoading(true);

      const endpoint = '/auth/verifyCode';
      const payload = { email: email, code: value };
      const newAction = { actionProperties: { endpoint } };
      const response = await makePostCall(newAction, loanApplication, payload, dispatch, templateConfig);

      if (response?.data) {
        const applications = Array.isArray(response.data) ? response.data : [response.data];
        setApplications(applications);
        setValidated(true);
        setCanStartNewApp(response.data.canStartNewApp);

        if (applications.length > 0) {
          setStep(2);
        } else {
          console.warn('No applications found in response');
          setStep(2);
        }
      } else {
        console.error('Invalid response format: missing data property');
        setValidated(false);
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      setError(error);
      setOpenErrorModal(true);
      setValidated(true);
    } finally {
      setIsLoading(false);
    }
  }, [setApplications, setStep, isValid, email, value, loanApplication, dispatch, templateConfig, setCanStartNewApp]);

  const handleResendCode = useCallback(async () => {
    if (timer === 0) {
      await sendCodeToEmail();
      setTimer(60);
    }
  }, [sendCodeToEmail, timer]);

  const getConfig = useCallback(({ existing = false, isValidation = false }: Options = {}): Config => {
    if (existing) {
      return {
        title: 'Existing Application Found',
        description: 'We found an active application associated with your information.',
        descriptionSecondary: 'We found an active application associated with your information.',
        labelBTN: 'Continue',
      };
    }

    if (isValidation) {
      return {
        title: 'Invalid Code',
        description: 'The verification code does not match.',
        descriptionSecondary: 'Please check your email and try again.',
        labelBTN: 'Continue',
      };
    }

    return {
      title: 'We Found Your Application',
      description: 'You have one or more applications in progress.',
      descriptionSecondary: "We've sent a verification code to your email to resume.",
      labelBTN: 'Continue',
    };
  }, []);

  useEffect(() => {
    let countdown: NodeJS.Timeout;
    if (timer > 0) {
      countdown = setTimeout(() => setTimer((prev) => prev - 1), 1000);
    }
    return () => clearTimeout(countdown);
  }, [timer]);

  const currentConfig = useMemo(() => {
    return getConfig({
      existing: existingApplication,
      isValidation: validated,
    });
  }, [existingApplication, getConfig, validated]);

  const handleCodeChange = useCallback(
    (values: string | number) => {
      const stringValue = values.toString();
      const normalized = stringValue.replace(/[^a-zA-Z0-9]/g, '').slice(0, 6);
      setValue(normalized);
      checkValidation({ 'resume-code-input': normalized });
    },
    [checkValidation],
  );

  return (
    <React.Fragment>
      <Container>
        <Title>{currentConfig.title}</Title>
        <Content>
          <BodyText>{currentConfig.description}</BodyText>
          <BodyText>{currentConfig.descriptionSecondary}</BodyText>
        </Content>
        <WrapperInput $isHidden={existingApplication}>
          <TextInput
            propertyType="code-input"
            dataTestId="resume-code-input"
            placeholder={VERIFICATION_CODE_CONFIG.text}
            isCentered
            size="50%"
            onChange={handleCodeChange}
            isHidden={VERIFICATION_CODE_CONFIG.isHidden}
          />
        </WrapperInput>
        <Footer>
          <Button label={isLoading ? <Spinner /> : 'Continue'} variant="primary" buttonSize="s" isDisabled={!isValid || isLoading} onClick={verifyCode} />
          <Button
            label={timer > 0 ? `Resend in ${timer}s` : VERIFICATION_CODE_CONFIG.buttonRestart.text}
            onClick={handleResendCode}
            variant="primary-link"
            buttonSize="l"
            isDisabled={timer > 0}
            isHidden={VERIFICATION_CODE_CONFIG.buttonRestart.isHidden}
          />
        </Footer>
      </Container>
      {openErrorModal && !!error && (
        <DefaultModal
          openModal={openErrorModal}
          setOpenModal={setOpenErrorModal}
          isErrorModal={true}
          infoModal={{
            header: (error as any)?.errorResponse?.response?.error?.appActionTitle || 'Error',
            body: typeof error === 'string' ? error : String(error || 'An error occurred'),
            button: (error as any)?.errorResponse?.response?.error?.appActionButtonTitle || 'Return to %%lenderName%%',
          }}
          preventBackgroundInteraction={true}
        />
      )}
    </React.Fragment>
  );
};
